import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const fmtErr = (d) => {
  if (!d) return "Login failed. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return d?.msg || "Login failed.";
};

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back, Mike.");
      nav("/admin");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--apex-primary)]">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Apex Home Pro remodel"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--apex-primary)] via-[var(--apex-primary)]/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <img src="/apex-logo.png" alt="Apex" className="h-16 w-auto mb-4 bg-white rounded-md p-2 inline-block" />
          <p className="font-display font-black text-3xl text-white leading-tight">Admin Dashboard</p>
          <p className="text-slate-300 mt-2">Manage estimate requests from Lehigh Valley homeowners.</p>
        </div>
      </div>

      <div className="grid place-items-center p-6">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="grid place-items-center h-12 w-12 rounded-md bg-[var(--apex-accent)] text-white mb-6">
            <Lock size={22} />
          </div>
          <h1 className="font-display font-black text-3xl text-white">Sign in</h1>
          <p className="text-slate-400 mt-2 mb-8">Apex Home Pro Inc — staff access only.</p>

          <label className="block text-sm font-semibold text-slate-300 mb-1.5">Email</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="mike@apexhomeproinc.com"
            className="w-full h-12 rounded-sm bg-white/5 border border-white/15 text-white px-4 outline-none focus:border-[var(--apex-accent)] focus:ring-1 focus:ring-[var(--apex-accent)] mb-4"
            required
          />
          <label className="block text-sm font-semibold text-slate-300 mb-1.5">Password</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full h-12 rounded-sm bg-white/5 border border-white/15 text-white px-4 outline-none focus:border-[var(--apex-accent)] focus:ring-1 focus:ring-[var(--apex-accent)] mb-6"
            required
          />
          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white font-semibold h-12 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <>Sign in <ArrowRight size={18} /></>}
          </button>
          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/15" />
            <span className="text-xs text-slate-500">OR</span>
            <span className="h-px flex-1 bg-white/15" />
          </div>
          <button
            type="button"
            data-testid="google-signin"
            onClick={loginWithGoogle}
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-sm bg-white text-slate-800 font-semibold h-12 hover:bg-slate-100 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.9 2.4 30.4 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.1C12.2 13.2 17.6 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-3.9 6.8-9.7 6.8-17.4z"/>
              <path fill="#FBBC05" d="M10.4 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.9-6.1z"/>
              <path fill="#34A853" d="M24 48c6.4 0 11.9-2.1 15.8-5.8l-7.3-5.7c-2 1.4-4.7 2.3-8.5 2.3-6.4 0-11.8-3.7-13.6-9.9l-7.9 6.1C6.4 42.6 14.6 48 24 48z"/>
            </svg>
            Continue with Google
          </button>
          <a href="/" className="block text-center text-sm text-slate-400 hover:text-white mt-6 transition-colors">← Back to website</a>
        </motion.form>
      </div>
    </div>
  );
}
